/**
 * The report definition state machine used by the builder.
 *
 * State is exactly the JSON the backend accepts (ReportDefinition), so it can
 * be sent as-is to /reports/preview and saved verbatim. The reducer keeps the
 * invariants the backend enforces: when grouping, columns are the group refs;
 * removing a column or measure drops sorts that referenced it.
 */
import { groupRef, labelFor, measureAlias, splitRef, TEMPORAL_TYPES } from './catalog';
import { defaultOpForType, emptyValueFor, filterHasValue, shapeOf } from './filters';

export const ACTIONS = {
  LOAD: 'LOAD',
  SET_DATASET: 'SET_DATASET',
  ADD_COLUMN: 'ADD_COLUMN',
  REMOVE_COLUMN: 'REMOVE_COLUMN',
  MOVE_COLUMN: 'MOVE_COLUMN',
  ADD_GROUP_BY: 'ADD_GROUP_BY',
  REMOVE_GROUP_BY: 'REMOVE_GROUP_BY',
  MOVE_GROUP_BY: 'MOVE_GROUP_BY',
  SET_GROUP_BUCKET: 'SET_GROUP_BUCKET',
  ADD_MEASURE: 'ADD_MEASURE',
  ADD_ADHOC_MEASURE: 'ADD_ADHOC_MEASURE',
  REMOVE_MEASURE: 'REMOVE_MEASURE',
  SET_MEASURE_ALIAS: 'SET_MEASURE_ALIAS',
  ADD_FILTER: 'ADD_FILTER',
  UPDATE_FILTER: 'UPDATE_FILTER',
  REMOVE_FILTER: 'REMOVE_FILTER',
  ADD_SORT: 'ADD_SORT',
  UPDATE_SORT: 'UPDATE_SORT',
  REMOVE_SORT: 'REMOVE_SORT',
  TOGGLE_SORT_FROM_GRID: 'TOGGLE_SORT_FROM_GRID',
  SET_DATE_RANGE: 'SET_DATE_RANGE',
  SET_OPTION: 'SET_OPTION',
  SET_LIMIT: 'SET_LIMIT',
  REMOVE_MISSING: 'REMOVE_MISSING',
};

export function emptyDefinition(datasetNode) {
  if (!datasetNode) return null;
  const options = {};
  (datasetNode.options || []).forEach((o) => {
    options[o.id] = !!o.default;
  });
  return {
    dataset: datasetNode.key,
    columns: [...(datasetNode.default_columns || [])],
    filters: [],
    group_by: [],
    measures: [],
    sort: datasetNode.default_sort ? [{ by: datasetNode.default_sort.by, dir: datasetNode.default_sort.dir }] : [],
    date_range: { field: null, preset: 'this_month', start: null, end: null },
    options,
    limit: null,
  };
}

/** Normalise a saved definition (strings in group_by, missing keys) into builder state. */
export function normaliseDefinition(def) {
  if (!def) return null;
  return {
    dataset: def.dataset,
    columns: [...(def.columns || [])],
    filters: (def.filters || []).map((f) => ({ field: f.field, op: f.op, value: f.value ?? emptyValueFor(f.op) })),
    group_by: (def.group_by || []).map((g) => (typeof g === 'string' ? { field: splitRef(g).base, bucket: splitRef(g).bucket } : { field: g.field, bucket: g.bucket || null })),
    measures: (def.measures || []).map((m) => ({ id: m.id || null, field: m.field || null, agg: m.agg || null, alias: m.alias || null })),
    sort: (def.sort || []).map((s) => ({ by: s.by, dir: s.dir || 'asc' })),
    date_range: {
      field: def.date_range?.field || null,
      preset: def.date_range?.preset || 'this_month',
      start: def.date_range?.start || null,
      end: def.date_range?.end || null,
    },
    options: { ...(def.options || {}) },
    limit: def.limit ?? null,
  };
}

const move = (arr, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const copy = [...arr];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

const dropSorts = (sort, ids) => sort.filter((s) => !ids.includes(s.by));

const sortableIds = (state) => {
  const measureIds = state.measures.map(measureAlias);
  if (state.group_by.length) return [...state.group_by.map(groupRef), ...measureIds];
  return [...state.columns, ...measureIds];
};

export function definitionReducer(state, action) {
  switch (action.type) {
    case ACTIONS.LOAD:
      return normaliseDefinition(action.definition);

    case ACTIONS.SET_DATASET:
      return emptyDefinition(action.dataset);

    case ACTIONS.ADD_COLUMN: {
      if (!state || state.columns.includes(action.id)) return state;
      if (state.group_by.length) return state; // columns are the group refs while grouping
      return { ...state, columns: [...state.columns, action.id] };
    }
    case ACTIONS.REMOVE_COLUMN: {
      const columns = state.columns.filter((c) => c !== action.id);
      return { ...state, columns, sort: dropSorts(state.sort, [action.id]) };
    }
    case ACTIONS.MOVE_COLUMN:
      return { ...state, columns: move(state.columns, action.from, action.to) };

    case ACTIONS.ADD_GROUP_BY: {
      const { base, bucket } = splitRef(action.id);
      if (state.group_by.some((g) => g.field === base)) return state;
      const group_by = [...state.group_by, { field: base, bucket: action.bucket || bucket || null }];
      const columns = group_by.map(groupRef);
      return { ...state, group_by, columns, sort: dropSorts(state.sort, state.columns.filter((c) => !columns.includes(c))) };
    }
    case ACTIONS.REMOVE_GROUP_BY: {
      const removed = state.group_by[action.index];
      const group_by = state.group_by.filter((_, i) => i !== action.index);
      const columns = group_by.length ? group_by.map(groupRef) : state.columns.filter((c) => c !== groupRef(removed));
      return { ...state, group_by, columns, sort: dropSorts(state.sort, removed ? [groupRef(removed)] : []) };
    }
    case ACTIONS.MOVE_GROUP_BY: {
      const group_by = move(state.group_by, action.from, action.to);
      return { ...state, group_by, columns: group_by.map(groupRef) };
    }
    case ACTIONS.SET_GROUP_BUCKET: {
      const old = state.group_by[action.index];
      if (!old) return state;
      const updated = { ...old, bucket: action.bucket || null };
      const group_by = state.group_by.map((g, i) => (i === action.index ? updated : g));
      const columns = group_by.map(groupRef);
      const sort = state.sort.map((s) => (s.by === groupRef(old) ? { ...s, by: groupRef(updated) } : s));
      return { ...state, group_by, columns, sort };
    }

    case ACTIONS.ADD_MEASURE: {
      if (state.measures.some((m) => m.id === action.id)) return state;
      return { ...state, measures: [...state.measures, { id: action.id, field: null, agg: null, alias: null }] };
    }
    case ACTIONS.ADD_ADHOC_MEASURE: {
      const spec = { id: null, field: action.field, agg: action.agg, alias: null };
      if (state.measures.some((m) => measureAlias(m) === measureAlias(spec))) return state;
      return { ...state, measures: [...state.measures, spec] };
    }
    case ACTIONS.REMOVE_MEASURE: {
      const removed = state.measures[action.index];
      return {
        ...state,
        measures: state.measures.filter((_, i) => i !== action.index),
        sort: removed ? dropSorts(state.sort, [measureAlias(removed)]) : state.sort,
      };
    }
    case ACTIONS.SET_MEASURE_ALIAS: {
      const old = state.measures[action.index];
      if (!old) return state;
      const alias = action.alias ? action.alias.trim() : null;
      const updated = { ...old, alias: alias || null };
      const sort = state.sort.map((s) => (s.by === measureAlias(old) ? { ...s, by: measureAlias(updated) } : s));
      return { ...state, measures: state.measures.map((m, i) => (i === action.index ? updated : m)), sort };
    }

    case ACTIONS.ADD_FILTER: {
      const op = action.op || defaultOpForType(action.fieldType, action.opsByType);
      return { ...state, filters: [...state.filters, { field: action.field || '', op, value: emptyValueFor(op) }] };
    }
    case ACTIONS.UPDATE_FILTER: {
      const old = state.filters[action.index];
      if (!old) return state;
      let next = { ...old, ...action.patch };
      if (action.patch.field !== undefined && action.patch.field !== old.field) {
        const op = action.patch.op || defaultOpForType(action.fieldType, action.opsByType);
        next = { field: action.patch.field, op, value: emptyValueFor(op) };
      } else if (action.patch.op !== undefined && shapeOf(action.patch.op) !== shapeOf(old.op)) {
        next.value = emptyValueFor(action.patch.op);
      }
      return { ...state, filters: state.filters.map((f, i) => (i === action.index ? next : f)) };
    }
    case ACTIONS.REMOVE_FILTER:
      return { ...state, filters: state.filters.filter((_, i) => i !== action.index) };

    case ACTIONS.ADD_SORT: {
      const candidates = sortableIds(state).filter((id) => !state.sort.some((s) => s.by === id));
      const by = action.by || candidates[0];
      if (!by) return state;
      return { ...state, sort: [...state.sort, { by, dir: action.dir || 'asc' }] };
    }
    case ACTIONS.UPDATE_SORT:
      return { ...state, sort: state.sort.map((s, i) => (i === action.index ? { ...s, ...action.patch } : s)) };
    case ACTIONS.REMOVE_SORT:
      return { ...state, sort: state.sort.filter((_, i) => i !== action.index) };
    case ACTIONS.TOGGLE_SORT_FROM_GRID: {
      const current = state.sort.length === 1 && state.sort[0].by === action.by ? state.sort[0] : null;
      if (!current) return { ...state, sort: [{ by: action.by, dir: 'asc' }] };
      if (current.dir === 'asc') return { ...state, sort: [{ by: action.by, dir: 'desc' }] };
      return { ...state, sort: [] };
    }

    case ACTIONS.SET_DATE_RANGE: {
      const date_range = { ...state.date_range, ...action.patch };
      if (date_range.preset !== 'custom') {
        date_range.start = null;
        date_range.end = null;
      }
      return { ...state, date_range };
    }
    case ACTIONS.SET_OPTION:
      return { ...state, options: { ...state.options, [action.id]: !!action.value } };
    case ACTIONS.SET_LIMIT:
      return { ...state, limit: action.limit ? Number(action.limit) : null };

    case ACTIONS.REMOVE_MISSING: {
      const id = action.id;
      const { base } = splitRef(id);
      const group_by = state.group_by.filter((g) => g.field !== base);
      const columns = state.columns.filter((c) => splitRef(c).base !== base);
      return {
        ...state,
        group_by,
        columns: group_by.length ? group_by.map(groupRef) : columns,
        filters: state.filters.filter((f) => f.field !== base),
        measures: state.measures.filter((m) => m.id !== id && m.field !== base),
        sort: state.sort.filter((s) => splitRef(s.by).base !== base && s.by !== id),
        date_range: state.date_range.field === base ? { ...state.date_range, field: null } : state.date_range,
      };
    }
    default:
      return state;
  }
}

export function hashDefinition(def) {
  return JSON.stringify(def);
}

/**
 * Client-side mirror of the backend validation. Returns per-section errors,
 * the ids the catalog no longer knows, and an overall flag.
 */
export function validateDefinition(def, index, catalog) {
  const errors = { dataset: [], columns: [], group_by: [], measures: [], filters: [], sort: [], date_range: [] };
  const missing = new Set();
  if (!def || !def.dataset) {
    errors.dataset.push('Pick a dataset');
    return { errors, missingIds: [], isValid: false };
  }
  const opsByType = catalog?.ops_by_type || {};
  const grouped = def.group_by.length > 0;
  const groupRefs = def.group_by.map(groupRef);

  def.group_by.forEach((g) => {
    const { entry, missing: isMissing } = labelFor(index, g.field);
    if (isMissing) missing.add(g.field);
    else if (entry.isRollup) errors.group_by.push(`${entry.label} is a number, not a dimension`);
    else if (entry.filterOnly) errors.group_by.push(`${entry.breadcrumb} can only be used in filters`);
    else if (g.bucket && !TEMPORAL_TYPES.has(entry.type)) errors.group_by.push(`${entry.label} is not a date and cannot be bucketed`);
  });

  def.columns.forEach((c) => {
    const { entry, missing: isMissing } = labelFor(index, c);
    if (isMissing) {
      missing.add(c);
      return;
    }
    if (entry.filterOnly) errors.columns.push(`${entry.breadcrumb} can only be used in filters`);
    if (grouped && !groupRefs.includes(c)) errors.columns.push(`${entry.label} must be a group-by field when grouping`);
  });

  const aliases = new Set();
  def.measures.forEach((m, i) => {
    if (m.id) {
      if (!index.measureIndex.has(m.id)) missing.add(m.id);
    } else {
      const { entry, missing: isMissing } = labelFor(index, m.field);
      if (isMissing) missing.add(m.field);
      else {
        const allowed = catalog?.adhoc_aggs_by_type?.[entry.type] || [];
        if (!allowed.includes(m.agg)) errors.measures.push(`Measure ${i + 1}: ${m.agg} is not allowed on ${entry.label}`);
        if (entry.filterOnly) errors.measures.push(`Measure ${i + 1}: ${entry.breadcrumb} can only be used in filters`);
      }
    }
    const alias = measureAlias(m);
    if (aliases.has(alias)) errors.measures.push(`Alias "${alias}" is used twice`);
    if (def.columns.includes(alias)) errors.measures.push(`Alias "${alias}" collides with a column`);
    aliases.add(alias);
  });
  if (grouped && def.measures.length === 0) errors.measures.push('Grouped reports need at least one measure');
  if (!grouped && def.columns.length === 0 && def.measures.length === 0) errors.columns.push('Pick at least one column or measure');

  def.filters.forEach((f, i) => {
    if (!f.field) {
      errors.filters.push(`Filter ${i + 1}: choose a field`);
      return;
    }
    const { entry, missing: isMissing } = labelFor(index, f.field);
    if (isMissing) {
      missing.add(f.field);
      return;
    }
    const ops = opsByType[entry.type] || [];
    if (ops.length && !ops.includes(f.op)) errors.filters.push(`Filter ${i + 1}: "${f.op}" does not apply to ${entry.label}`);
    if (!filterHasValue(f)) errors.filters.push(`Filter ${i + 1}: enter a value for ${entry.label}`);
    if (shapeOf(f.op) === 'pair' && filterHasValue(f) && String(f.value[0]) > String(f.value[1]) && entry.type !== 'string') {
      const [lo, hi] = f.value.map(Number);
      if (!Number.isNaN(lo) && !Number.isNaN(hi) && lo > hi) errors.filters.push(`Filter ${i + 1}: the low value is greater than the high value`);
    }
  });

  const sortable = new Set(sortableIds(def));
  def.sort.forEach((s, i) => {
    if (!sortable.has(s.by)) {
      const { missing: isMissing, entry } = labelFor(index, s.by);
      if (isMissing && !aliases.has(s.by)) missing.add(s.by);
      else if (grouped) errors.sort.push(`Sort ${i + 1}: ${s.by} must be a group-by field or a measure`);
      else if (entry && !entry.sortable) errors.sort.push(`Sort ${i + 1}: ${entry.label} cannot be sorted`);
    }
  });

  const dr = def.date_range || {};
  if (dr.preset === 'custom') {
    if (!dr.start || !dr.end) errors.date_range.push('Custom range needs both start and end dates');
    else if (dr.start > dr.end) errors.date_range.push('Start date is after end date');
  }
  if (dr.field) {
    const { entry, missing: isMissing } = labelFor(index, dr.field);
    if (isMissing) missing.add(dr.field);
    else if (!TEMPORAL_TYPES.has(entry.type) || entry.viaOneToMany || entry.filterOnly) errors.date_range.push(`${entry.label} is not a date field on the rows`);
  }

  const missingIds = [...missing];
  const hasErrors = Object.values(errors).some((list) => list.length > 0);
  return { errors, missingIds, isValid: !hasErrors && missingIds.length === 0 };
}

/** Section name for a backend error string such as "filters[2]: ..." or "group_by: ...". */
export function sectionOfError(message) {
  const m = /^(dataset|columns|group_by|measures|filters|sort|date_range|options)/.exec(String(message || ''));
  return m ? m[1] : null;
}
